import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

/**
 * ModelSpotlight — Apple-style cinematic showcase.
 * - Machine sits under a dramatic top-lit spotlight
 * - As the user scrolls through the section, the machine subtly rotates (rotateY/rotateX),
 *   scales and translates — feels like the machine is "in the spotlight"
 * - Left/Right arrows cycle between featured models
 */
export default function ModelSpotlight({ models }) {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Scroll-linked 3D transforms
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [-18, 0, 18]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [6, -2, 8]);
  const translateY = useTransform(scrollYProgress, [0, 0.5, 1], [40, 0, -40]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.92, 1, 0.95]);

  const active = models[idx];

  const next = () => {
    setDir(1);
    setIdx((i) => (i + 1) % models.length);
  };
  const prev = () => {
    setDir(-1);
    setIdx((i) => (i - 1 + models.length) % models.length);
  };

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [models.length]);

  if (!active) return null;

  return (
    <section
      ref={sectionRef}
      id="spotlight"
      className="relative bg-black overflow-hidden"
      data-testid="model-spotlight"
      style={{ perspective: "1600px" }}
    >
      {/* Grain + stage light */}
      <div className="grain" />
      <div className="spotlight" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
        {/* Heading */}
        <div className="text-center mb-8 md:mb-12">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#ff6a13]">
            Under the Spotlight
          </div>
          <h2 className="h2-section text-white text-4xl md:text-6xl mt-3">
            One tough animal.
            <br />
            <span className="text-white/50">Many disciplines.</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-xl mx-auto">
            Cycle through the current Bobcat India lineup. Scroll to rotate, arrows to swipe.
          </p>
        </div>

        {/* Stage */}
        <div className="relative h-[460px] md:h-[560px] flex items-center justify-center">
          <div className="spotlight-floor" />

          {/* Machine image with scroll-linked 3D transform */}
          <motion.div
            key={active.id}
            initial={{ opacity: 0, x: dir * 80, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -dir * 80 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 will-change-transform"
            style={{
              rotateY,
              rotateX,
              y: translateY,
              scale,
              transformStyle: "preserve-3d",
            }}
          >
            <img
              src={active.image}
              alt={active.name}
              draggable={false}
              className="no-drag w-[560px] md:w-[820px] max-w-[90vw] h-auto drop-shadow-[0_60px_40px_rgba(0,0,0,0.6)]"
              data-testid={`spotlight-image-${active.id}`}
            />
          </motion.div>

          {/* Category eyebrow floating */}
          <div
            className="absolute left-4 md:left-10 top-4 md:top-8 text-[11px] tracking-[0.3em] uppercase font-bold text-white/60"
            data-testid="spotlight-category"
          >
            {active.category}
          </div>

          {/* Model # indicator */}
          <div className="absolute right-4 md:right-10 top-4 md:top-8 text-[11px] tracking-[0.3em] uppercase font-bold text-white/50">
            {String(idx + 1).padStart(2, "0")} / {String(models.length).padStart(2, "0")}
          </div>

          {/* Arrow controls */}
          <button
            onClick={prev}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-[#ff6a13] border border-white/20 backdrop-blur flex items-center justify-center text-white transition-all"
            aria-label="Previous model"
            data-testid="spotlight-prev"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={next}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-[#ff6a13] border border-white/20 backdrop-blur flex items-center justify-center text-white transition-all"
            aria-label="Next model"
            data-testid="spotlight-next"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        {/* Model info row */}
        <motion.div
          key={`info-${active.id}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-6 md:mt-10 grid lg:grid-cols-12 gap-6 items-end"
        >
          <div className="lg:col-span-7">
            <div className="font-display text-4xl md:text-6xl font-black text-white tracking-tight">
              {active.name}
            </div>
            <p className="text-white/60 mt-3 max-w-2xl">{active.long_desc}</p>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
              {Object.entries(active.specs).slice(0, 3).map(([k, v]) => (
                <div key={k} className="bg-black px-4 py-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                    {k}
                  </div>
                  <div className="font-display text-lg md:text-xl font-bold text-white mt-1.5">
                    {v}
                  </div>
                </div>
              ))}
            </div>
            <Link
              to={`/models/${active.id}`}
              className="mt-5 inline-flex items-center gap-2 bg-[#ff6a13] text-white font-semibold px-5 py-3 rounded-full hover:bg-[#e85a0d] transition-colors"
              data-testid={`spotlight-view-${active.id}`}
            >
              Explore the {active.code} <ArrowUpRight size={16} />
            </Link>
          </div>
        </motion.div>

        {/* Dots */}
        <div className="mt-10 flex items-center justify-center gap-2">
          {models.map((m, i) => (
            <button
              key={m.id}
              onClick={() => {
                setDir(i > idx ? 1 : -1);
                setIdx(i);
              }}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "bg-[#ff6a13] w-10" : "bg-white/20 w-4 hover:bg-white/40"
              }`}
              aria-label={`Go to ${m.code}`}
              data-testid={`spotlight-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
